import React from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from 'react-router-dom';
import { AuthCallback } from 'src/routes/AuthCallback';
import { SignOut } from 'src/routes/SignOut';
import { Swagger } from 'src/routes/Swagger';
import { Root } from './Root';
import { CampaignHome } from './CampaignHome';
import { CampaignDetails } from './CampaignDetails';
import { GlobalErrorBoundary } from './GlobalErrorBoundary';
import { BlocklistList } from './BlocklistList';
import { BlocklistDetail } from './BlocklistDetail';
import { BlocklistCreate } from './BlocklistCreate';
import { ReportingDashboard } from './ReportingDashboard';
import { StandaloneAssets } from './StandaloneAssets';
import { SpotlightLalAudienceCreation } from './SpotlightLalAudienceCreation';
import { TitlesTab } from './TitlesTab';
import { PublisherConfigList } from './PublisherConfigList';
import { PublisherConfigCreate } from './PublisherConfigCreate';
import { PublisherConfigDetail } from './PublisherConfigDetail';
import { BoxFolders } from './BoxFolders';
import { SocialSignals } from './SocialSignals';
import { EntityMetrics } from './EntityMetrics';
import TikTokDashboard from './TikTokDashboard';
import MetaDashboard from './MetaDashboard';
import GoogleDashboard from './GoogleDashboard';
import AdspDashboard from './AdspDashboard';
import { BuManagement } from './BuManagement';
import { AccessManagement } from './AccessManagement';
import { isFeatureEnabled } from 'src/feature-flags';
import { FEATURE_FLAGS } from 'src/constants';
import { useUser } from 'src/contexts/UserContext';
import { canAccessPaidMediaDashboards } from 'src/hooks/insightsAccess';
import { CampaignPublishers } from 'src/store/constants';
import { InsightsAccessGuard } from './InsightsAccessGuard';

export const ReportingDashboardRoute = () => {
  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_REPORTING_DASHBOARD)) {
    return <Navigate replace to="/" />;
  }
  return <ReportingDashboard />;
};

export const BoxFoldersRoute = () => {
  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_BOX_FOLDERS)) {
    return <Navigate replace to="/" />;
  }
  return (
    <InsightsAccessGuard internalOnly>
      <BoxFolders />
    </InsightsAccessGuard>
  );
};

export const BlocklistRoute = () => {
  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST)) {
    return <Navigate replace to="/" />;
  }
  return <BlocklistList />;
};

export const BlocklistDetailRoute = () => {
  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST)) {
    return <Navigate replace to="/" />;
  }
  return <BlocklistDetail />;
};

export const BlocklistCreateRoute = () => {
  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_BLOCKLIST)) {
    return <Navigate replace to="/" />;
  }
  return <BlocklistCreate />;
};

export const SpotlightLalRoute = (): React.ReactElement => {
  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_SPOTLIGHT_LAL)) {
    return <Navigate replace to="/" />;
  }
  return (
    <InsightsAccessGuard internalOnly>
      <SpotlightLalAudienceCreation />
    </InsightsAccessGuard>
  );
};

export const SocialSignalsRoute = (): React.ReactElement => {
  if (!isFeatureEnabled(FEATURE_FLAGS.ENABLE_SOCIAL_SIGNALS)) {
    return <Navigate replace to="/" />;
  }
  return (
    <InsightsAccessGuard internalOnly>
      <SocialSignals />
    </InsightsAccessGuard>
  );
};

export const TikTokDashboardRoute = (): React.ReactElement | null => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !canAccessPaidMediaDashboards(user) || !isFeatureEnabled(FEATURE_FLAGS.ENABLE_PAID_MEDIA_DASHBOARD)) {
    return <Navigate replace to="/" />;
  }
  return (
    <InsightsAccessGuard publisher={CampaignPublishers.TIKTOK}>
      <TikTokDashboard />
    </InsightsAccessGuard>
  );
};

export const MetaDashboardRoute = (): React.ReactElement | null => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !canAccessPaidMediaDashboards(user) || !isFeatureEnabled(FEATURE_FLAGS.ENABLE_PAID_MEDIA_DASHBOARD)) {
    return <Navigate replace to="/" />;
  }
  return (
    <InsightsAccessGuard publisher={CampaignPublishers.META}>
      <MetaDashboard />
    </InsightsAccessGuard>
  );
};

export const GoogleDashboardRoute = (): React.ReactElement | null => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (
    isError ||
    !canAccessPaidMediaDashboards(user) ||
    !isFeatureEnabled(FEATURE_FLAGS.ENABLE_PAID_MEDIA_DASHBOARD) ||
    !isFeatureEnabled(FEATURE_FLAGS.ENABLE_GOOGLE_DEMANDGEN)
  ) {
    return <Navigate replace to="/" />;
  }
  return (
    <InsightsAccessGuard publisher={CampaignPublishers.GOOGLE}>
      <GoogleDashboard />
    </InsightsAccessGuard>
  );
};

export const AdspDashboardRoute = (): React.ReactElement | null => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !canAccessPaidMediaDashboards(user) || !isFeatureEnabled(FEATURE_FLAGS.ENABLE_PAID_MEDIA_DASHBOARD)) {
    return <Navigate replace to="/" />;
  }
  return (
    <InsightsAccessGuard publisher={CampaignPublishers.ADSP}>
      <AdspDashboard />
    </InsightsAccessGuard>
  );
};

export const PublisherConfigRoute = () => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !user?.isAdmin) {
    return <Navigate replace to="/" />;
  }
  return <PublisherConfigList />;
};

export const PublisherConfigCreateRoute = () => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !user?.isAdmin) {
    return <Navigate replace to="/" />;
  }
  return <PublisherConfigCreate />;
};

export const PublisherConfigDetailRoute = () => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !user?.isAdmin) {
    return <Navigate replace to="/" />;
  }
  return <PublisherConfigDetail />;
};

export const EntityMetricsRoute = () => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !user?.isAdmin || !isFeatureEnabled(FEATURE_FLAGS.ENABLE_ENTITY_METRICS)) {
    return <Navigate replace to="/" />;
  }
  return <EntityMetrics />;
};

export const BuManagementRoute = () => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !user?.isAdmin) {
    return <Navigate replace to="/" />;
  }
  return <BuManagement />;
};

export const AccessManagementRoute = () => {
  const { user, isLoading, isError } = useUser();
  if (isLoading) return null;
  if (isError || !user) {
    return <Navigate replace to="/" />;
  }
  // BU admins and platform admins can access; the page itself handles the scoping
  return <AccessManagement />;
};

const loadDocsRoute = async () => {
  const { Docs } = await import('./Docs');
  return { Component: Docs };
};

export const routes = createRoutesFromElements(
  <>
    <Route element={<Root />} errorElement={<GlobalErrorBoundary />} path="/">
      <Route element={<CampaignHome />} path="/campaigns" />
      <Route element={<CampaignDetails />} path="/campaigns/:campaignId" />
      <Route element={<Navigate replace to="/campaigns" />} path="/" />
      <Route element={<Swagger />} path="/api-docs" />

      <Route element={<BlocklistRoute />} path="blocklists" />
      <Route element={<BlocklistCreateRoute />} path="blocklists/create" />
      <Route element={<BlocklistDetailRoute />} path="blocklists/:id" />

      <Route element={<PublisherConfigRoute />} path="publisher-configs" />
      <Route element={<PublisherConfigCreateRoute />} path="publisher-configs/create" />
      <Route element={<PublisherConfigDetailRoute />} path="publisher-configs/:id" />

      <Route element={<BoxFoldersRoute />} path="/box-folders" />

      <Route element={<ReportingDashboardRoute />} path="/reporting-dashboard" />
      <Route element={<EntityMetricsRoute />} path="/entity-metrics" />
      <Route element={<BuManagementRoute />} path="/bu-management" />
      <Route element={<AccessManagementRoute />} path="/access-management" />
      <Route element={<StandaloneAssets />} path="/assets" />
      <Route element={<SpotlightLalRoute />} path="/spotlight-lal" />
      <Route element={<TitlesTab />} path="/titles" />
      <Route element={<SocialSignalsRoute />} path="/social-signals" />
      <Route element={<TikTokDashboardRoute />} path="/tiktok-ads-performance" />
      <Route element={<MetaDashboardRoute />} path="/meta-ads-performance" />
      <Route element={<GoogleDashboardRoute />} path="/google-ads-performance" />
      <Route element={<AdspDashboardRoute />} path="/adsp-ads-performance" />

      <Route lazy={loadDocsRoute} path="/user-guide" />
      <Route lazy={loadDocsRoute} path="/user-guide/:folder/:page" />
    </Route>

    <Route element={<AuthCallback />} path="/auth-callback" />
    <Route element={<SignOut />} path="/signout" />
  </>
);

export default createBrowserRouter(routes);
